import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ScanLine, Check, Info } from "lucide-react";
import { useAuth } from "@/App";

/**
 * ManualBiRadsCard — карта за РЪЧНО въвеждане на BI-RADS категория от
 * реален радиологичен (мамографски) доклад.
 *
 * iter698 — В Nutramilo плътността на гърдата вече се определя САМО от
 * ръчно въведения BI-RADS от пациента (не от софтуерно/AI сканиране).
 *
 * Props:
 *  - onSaved: callback (birads) → invokes parent refresh after save
 *  - lang: "bg" | "en" | "es"
 *  - isDark: boolean
 */
const TX = {
  bg: {
    title:     "Категория BI-RADS (от Вашия мамографски доклад)",
    subtitle:  "Въведете категорията, която пише в писмения доклад от радиолог. Софтуерно автоматично разпознаване НЕ се прави.",
    label:     "Категория плътност",
    percent:   "Процент плътност (по желание)",
    reportDate:"Дата на доклада (по желание)",
    save:      "Запази",
    saved:     "Запазено · BI-RADS",
    optionA:   "A — Почти изцяло мазнина",
    optionB:   "B — Разпръснати жлезисти зони",
    optionC:   "C — Хетерогенно плътна",
    optionD:   "D — Изключително плътна",
    hint:      "Свържете се с Вашия мамолог за категорията A/B/C/D. Тази стойност се използва в AI обобщението на BHI.",
    none:      "Все още не сте въвели категория.",
  },
  en: {
    title:     "BI-RADS category (from your radiologist's report)",
    subtitle:  "Enter the category written in your written radiologist report. NO software/AI auto-detection is performed.",
    label:     "Density category",
    percent:   "Percent density (optional)",
    reportDate:"Report date (optional)",
    save:      "Save",
    saved:     "Saved · BI-RADS",
    optionA:   "A — Almost entirely fat",
    optionB:   "B — Scattered fibroglandular",
    optionC:   "C — Heterogeneously dense",
    optionD:   "D — Extremely dense",
    hint:      "Ask your mammographer for the A/B/C/D category. This value is used in the BHI AI summary.",
    none:      "No category entered yet.",
  },
  es: {
    title:     "Categoría BI-RADS (de su informe de radiólogo)",
    subtitle:  "Introduzca la categoría que aparece en el informe escrito. NO se realiza autodetección por software/IA.",
    label:     "Categoría de densidad",
    percent:   "Porcentaje de densidad (opcional)",
    reportDate:"Fecha del informe (opcional)",
    save:      "Guardar",
    saved:     "Guardado · BI-RADS",
    optionA:   "A — Casi totalmente grasa",
    optionB:   "B — Densidades fibroglandulares dispersas",
    optionC:   "C — Heterogéneamente densa",
    optionD:   "D — Extremadamente densa",
    hint:      "Pida la categoría A/B/C/D a su mamografista. Este valor alimenta el Perfil Combinado de Riesgo.",
    none:      "Aún no se ha introducido categoría.",
  },
};

export default function ManualBiRadsCard({ onSaved, lang = "bg", isDark = false }) {
  const { API } = useAuth();
  const t = TX[lang] || TX.bg;
  const [birads, setBirads] = useState("");
  const [percent, setPercent] = useState("");
  const [reportDate, setReportDate] = useState("");
  const [savedBirads, setSavedBirads] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/breast-intelligence/manual-birads`);
        if (!alive) return;
        if (data?.birads) {
          setBirads(data.birads);
          setSavedBirads(data.birads);
          if (data.percent_density != null) setPercent(String(data.percent_density));
          if (data.report_date) setReportDate(data.report_date);
          if (data.saved_at) setSavedAt(data.saved_at);
        }
      } catch (_) {
        /* ignore */
      }
    })();
    return () => { alive = false; };
  }, [API]);

  const handleSave = async () => {
    if (!["A", "B", "C", "D"].includes(birads)) {
      toast.error(t.label);
      return;
    }
    setSaving(true);
    try {
      const body = { birads };
      if (percent) body.percent_density = Number(percent);
      if (reportDate) body.report_date = reportDate;
      const { data } = await axios.post(`${API}/breast-intelligence/manual-birads`, body);
      setSavedBirads(data.birads);
      setSavedAt(new Date().toISOString());
      toast.success(`${t.saved} ${data.birads} ✓`);
      // iter707b — emit a window event so other on-page widgets (e.g. MyDataHub)
      // can flip their status pill to "complete" immediately without waiting
      // for the parent snapshot refetch.
      try { window.dispatchEvent(new CustomEvent("bhi:birads:saved", { detail: { birads: data.birads } })); } catch (_) { /* noop */ }
      onSaved?.(data.birads);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
  }`;

  return (
    <div className={`${cardCls} p-5`} data-testid="manual-birads-card">
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          isDark ? "bg-pink-500/20" : "bg-pink-100"
        }`}>
          <ScanLine className={`w-5 h-5 ${isDark ? "text-pink-300" : "text-pink-600"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-base">{t.title}</h3>
          <p className="text-xs opacity-70 mt-0.5">{t.subtitle}</p>
        </div>
        {savedBirads && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
              isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"
            }`}
            data-testid="manual-birads-saved-pill"
          >
            <Check className="w-3 h-3" />
            BI-RADS {savedBirads}
          </span>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold opacity-70 block mb-1">{t.label}</label>
          <select
            value={birads}
            onChange={(e) => setBirads(e.target.value)}
            data-testid="manual-birads-select"
            className={`w-full rounded-lg border px-3 py-2 text-sm ${
              isDark
                ? "bg-[hsl(222,47%,8%)] border-[hsl(223,47%,22%)] text-white"
                : "bg-white border-gray-300 text-gray-900"
            }`}
          >
            <option value="">—</option>
            <option value="A">{t.optionA}</option>
            <option value="B">{t.optionB}</option>
            <option value="C">{t.optionC}</option>
            <option value="D">{t.optionD}</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold opacity-70 block mb-1">{t.percent}</label>
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            placeholder="—"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            data-testid="manual-birads-percent"
            className={`w-full rounded-lg border px-3 py-2 text-sm ${
              isDark
                ? "bg-[hsl(222,47%,8%)] border-[hsl(223,47%,22%)] text-white"
                : "bg-white border-gray-300 text-gray-900"
            }`}
          />
        </div>

        <div>
          <label className="text-xs font-semibold opacity-70 block mb-1">{t.reportDate}</label>
          <input
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
            data-testid="manual-birads-report-date"
            className={`w-full rounded-lg border px-3 py-2 text-sm ${
              isDark
                ? "bg-[hsl(222,47%,8%)] border-[hsl(223,47%,22%)] text-white"
                : "bg-white border-gray-300 text-gray-900"
            }`}
          />
        </div>

        <div className="flex items-end">
          <button
            onClick={handleSave}
            disabled={!birads || saving}
            data-testid="manual-birads-save-btn"
            className={`w-full px-4 py-2 rounded-lg font-semibold text-sm transition ${
              !birads || saving
                ? "opacity-50 cursor-not-allowed bg-gray-300 text-gray-500"
                : "bg-pink-600 text-white hover:bg-pink-700"
            }`}
          >
            {saving ? "…" : t.save}
          </button>
        </div>
      </div>

      <div className={`mt-3 flex items-start gap-2 p-2.5 rounded-lg text-[11px] leading-relaxed ${
        isDark ? "bg-amber-500/10 text-amber-200" : "bg-amber-50 text-amber-800"
      }`}>
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>{t.hint}</span>
      </div>

      {!savedBirads && (
        <p className="text-[11px] opacity-50 mt-2" data-testid="manual-birads-none">{t.none}</p>
      )}
      {savedBirads && savedAt && (
        <p className="text-[11px] opacity-50 mt-2">
          {lang === "bg" ? "Запазено:" : lang === "es" ? "Guardado:" : "Saved:"}{" "}
          {new Date(savedAt).toLocaleString(lang === "bg" ? "bg-BG" : lang === "es" ? "es-ES" : "en-US")}
        </p>
      )}
    </div>
  );
}
